import { api } from "@/api/client";

export interface Plan {
  id: string;
  display_name: string;
  monthly_price_usd: number;
  rps_limit: number;
  // Serialized as a string because it's a BigInt server-side. Convert with
  // Number(...) before calling .toLocaleString() — calling that on a raw
  // string is a no-op and leaves "1000000" without thousand separators.
  monthly_credits: string;
  cycle_days: number;
  sse_connections_max: number;
  max_tokens_per_sse_conn: number;
  overage_credit_price_usd: number;
  hidden: boolean;
}

export interface Payment {
  id: string;
  amount_usdc: number;
  tx_signature: string | null;
  from_wallet: string | null;
  received_at: string;
  status: "pending" | "confirmed" | "refunded" | "rejected";
  plan_id: string | null;
  extends_to: string | null;
  note: string | null;
  applied_promo_code: string | null;
  list_price_usd: string | null;
  discount_usd: string | null;
}

export type PromoKind = "percent" | "fixed_usd";
export type PromoScope = "once" | "lifetime";

export interface ActivePromo {
  code: string;
  kind: PromoKind;
  value: string;
  scope: PromoScope;
}

export interface PromoValidateResult {
  ok: boolean;
  code?: string;
  kind?: PromoKind;
  value?: string;
  scope?: PromoScope;
  list_price_usd?: string;
  discount_usd?: string;
  net_usd?: string;
  is_comp?: boolean;
  reason?: string;
  message?: string;
}

export interface QueuedRenewal {
  // 'confirmed' = paid + queued; 'pending' = started but not yet paid.
  status: "pending" | "confirmed";
  payment_id: string;
  // The plan this payment buys; differs from the current plan for a plan change.
  plan_id: string | null;
  applies_at: string;
  extends_to: string;
  amount_usdc: string;
}

export interface ProjectPlan {
  plan: Plan;
  paid_until: string | null;
  queued_renewal: QueuedRenewal | null;
  active_promo: ActivePromo | null;
}

export interface RenewResponse {
  ok: true;
  payment_id: string;
  applies_at: string;
  extends_to: string;
  amount_usdc: string;
  is_comp: boolean;
  applied_promo_code: string | null;
  discount_usd: string | null;
}

export interface PlanChangeResponse extends RenewResponse {
  requested_plan: string;
}

export interface OnchainPayment {
  id: string;
  payment_id: string;
  project_id: string;
  autoincrement_id: number;
  public_key: string;
  status: "pending" | "received" | "expired";
  expires_at: string;
  amount_usdc: string;
  received_amount: string | null;
  received_at: string | null;
  received_tx_sig: string | null;
  created_at: string;
}

export const plansApi = {
  list: () => api.get<Plan[]>("/plans"),
  forProject: (pid: string) => api.get<ProjectPlan>(`/projects/${pid}/plan`),
  requestChange: (pid: string, plan_id: string, promo_code?: string) =>
    api.post<PlanChangeResponse>(`/projects/${pid}/plan`, {
      plan_id,
      ...(promo_code ? { promo_code } : {}),
    }),
  renew: (pid: string, promo_code?: string) =>
    api.post<RenewResponse>(`/projects/${pid}/renew`, promo_code ? { promo_code } : {}),
  payments: (pid: string) => api.get<Payment[]>(`/projects/${pid}/payments`),

  // Cancel a pending (unpaid) payment — a renewal or plan change the user
  // started but no longer wants. Frees them to start a different one.
  cancelPayment: (pid: string, payment_id: string) =>
    api.post<{ ok: true }>(`/projects/${pid}/payments/${payment_id}/cancel`, {}),

  // Promo code price preview (no side effects). context 'buy' | 'renew'.
  validatePromo: (code: string, plan_id: string, context: "buy" | "renew" = "buy") =>
    api.get<PromoValidateResult>("/promo/validate", {
      query: { code, plan_id, context },
    }),

  // On-chain payment helpers. POST is idempotent + extends the watch
  // window on each call. GET just polls current state.
  onchainRequest: (pid: string, payment_id: string) =>
    api.post<OnchainPayment>(`/projects/${pid}/payments/${payment_id}/onchain`, {}),
  onchainGet: (pid: string, payment_id: string) =>
    api.get<OnchainPayment>(`/projects/${pid}/payments/${payment_id}/onchain`),
};
